import { motion } from "framer-motion";
import { Heart, MessageCircle, Send, Bookmark } from "lucide-react";

export const LaptopMockup = () => (
  <div
    role="img"
    aria-label="Illustration av en modern företagshemsida på en laptop"
    className="w-full overflow-hidden rounded-2xl border border-white/10 bg-navy2 shadow-2xl shadow-black/40"
  >
    <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
      <span className="h-2.5 w-2.5 rounded-full bg-orange/70" />
      <span className="h-2.5 w-2.5 rounded-full bg-cream/30" />
      <span className="h-2.5 w-2.5 rounded-full bg-teal/70" />
      <div className="ml-3 flex-1 rounded-md bg-navy px-3 py-1 text-[10px] font-medium text-cream/50">tillvia.se</div>
    </div>
    <div className="bg-navy2 p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-5 w-5 rounded-md bg-teal" />
          <span className="h-2 w-12 rounded-full bg-cream/70" />
        </div>
        <div className="hidden items-center gap-2 sm:flex">
          <span className="h-1.5 w-8 rounded-full bg-cream/30" />
          <span className="h-1.5 w-8 rounded-full bg-cream/30" />
          <span className="h-1.5 w-8 rounded-full bg-cream/30" />
          <span className="h-6 w-16 rounded-md bg-orange" />
        </div>
      </div>
      <div className="mt-7 space-y-3">
        <div className="h-4 w-3/4 rounded-full bg-cream/90" />
        <div className="h-4 w-1/2 rounded-full bg-teal" />
        <div className="mt-4 space-y-2">
          <div className="h-1.5 w-2/3 rounded-full bg-slate2" />
          <div className="h-1.5 w-1/2 rounded-full bg-slate2" />
        </div>
        <div className="mt-5 flex gap-2.5">
          <span className="h-7 w-24 rounded-lg bg-orange" />
          <span className="h-7 w-24 rounded-lg border border-teal/60" />
        </div>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-xl border border-white/10 bg-navy p-3">
            <span className={`block h-6 w-6 rounded-lg ${i === 1 ? "bg-orange/80" : "bg-teal/70"}`} />
            <div className="mt-3 h-1.5 w-4/5 rounded-full bg-cream/40" />
            <div className="mt-1.5 h-1.5 w-3/5 rounded-full bg-slate2" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const PhoneMockup = () => (
  <div
    role="img"
    aria-label="Illustration av ett inlägg i sociala medier på en mobiltelefon"
    className="w-[168px] rounded-[26px] border border-white/10 bg-navy2 p-2 shadow-2xl shadow-black/50 sm:w-[190px]"
  >
    <div className="overflow-hidden rounded-[20px] bg-navy">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span className="h-6 w-6 rounded-full bg-teal" />
        <div>
          <div className="h-1.5 w-14 rounded-full bg-cream/70" />
          <div className="mt-1 h-1 w-9 rounded-full bg-slate2" />
        </div>
      </div>
      <div className="relative mx-1 h-32 overflow-hidden rounded-xl bg-teal sm:h-36">
        <span className="absolute -right-5 -top-5 h-20 w-20 rounded-full bg-orange/90" />
        <span className="absolute bottom-4 left-4 h-2 w-16 rounded-full bg-cream" />
        <span className="absolute bottom-8 left-4 h-2 w-10 rounded-full bg-cream/60" />
      </div>
      <div className="flex items-center gap-3 px-3 py-2.5 text-cream/70">
        <Heart size={13} className="text-orange" aria-hidden="true" />
        <MessageCircle size={13} aria-hidden="true" />
        <Send size={13} aria-hidden="true" />
        <Bookmark size={13} className="ml-auto" aria-hidden="true" />
      </div>
      <div className="space-y-1.5 px-3 pb-3.5">
        <div className="h-1.5 w-2/3 rounded-full bg-cream/40" />
        <div className="h-1.5 w-1/2 rounded-full bg-slate2" />
      </div>
    </div>
  </div>
);

export const FloatingChip = ({ icon: Icon, title, sub, className = "", delay = 0 }) => (
  <motion.div
    animate={{ y: [0, -8, 0] }}
    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay }}
    className={`flex items-center gap-3 rounded-xl border border-white/10 bg-navy2/95 px-4 py-3 shadow-xl shadow-black/30 backdrop-blur ${className}`}
  >
    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal/20 text-teal">
      <Icon size={18} aria-hidden="true" />
    </span>
    <span>
      <span className="block font-display text-sm font-bold text-white">{title}</span>
      <span className="block text-xs text-cream/50">{sub}</span>
    </span>
  </motion.div>
);

export const MenuMockup = ({ className = "" }) => (
  <div
    role="img"
    aria-label="Designexempel på en restaurangmeny"
    className={`rounded-xl border border-ice bg-white p-5 shadow-lg shadow-navy/5 ${className}`}
  >
    <p className="font-display text-center text-xs font-extrabold uppercase tracking-[0.35em] text-teal">Meny</p>
    <div className="mx-auto mt-2 h-0.5 w-8 bg-orange" />
    <div className="mt-4 space-y-3.5">
      {[
        ["w-3/5", "129 kr"],
        ["w-1/2", "145 kr"],
        ["w-2/3", "119 kr"],
      ].map(([w, pris], i) => (
        <div key={i} className="flex items-baseline justify-between gap-3">
          <span className={`h-2 rounded-full bg-slate2/50 ${w}`} />
          <span className="font-display text-xs font-bold text-navy">{pris}</span>
        </div>
      ))}
    </div>
  </div>
);

export const PosterMockup = ({ className = "" }) => (
  <div
    role="img"
    aria-label="Designexempel på en poster"
    className={`relative overflow-hidden rounded-xl bg-navy p-5 shadow-lg shadow-navy/20 ${className}`}
  >
    <span className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-orange" />
    <span className="absolute -bottom-6 -left-6 h-16 w-16 rounded-full border-2 border-teal" />
    <p className="font-display text-[10px] font-bold uppercase tracking-[0.3em] text-teal">Poster & affisch</p>
    <p className="mt-2 font-display text-2xl font-extrabold leading-tight text-cream">
      Nyhet
      <br />
      <span className="text-orange">i butik</span>
    </p>
    <div className="mt-3 h-1.5 w-3/4 rounded-full bg-cream/30" />
    <div className="mt-1.5 h-1.5 w-1/2 rounded-full bg-cream/20" />
  </div>
);

export const FlyerMockup = ({ className = "" }) => (
  <div
    role="img"
    aria-label="Designexempel på ett reklamblad"
    className={`flex items-center justify-between gap-4 rounded-xl border border-orange/30 bg-cream p-5 shadow-lg shadow-navy/5 ${className}`}
  >
    <div>
      <p className="font-display text-[10px] font-bold uppercase tracking-[0.3em] text-teal">Flyer & reklamblad</p>
      <p className="mt-1.5 font-display text-lg font-extrabold text-navy">Kampanjerbjudande</p>
      <div className="mt-2.5 h-1.5 w-32 rounded-full bg-slate2/30" />
    </div>
    <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-orange font-display text-lg font-extrabold text-navy">
      -20%
    </span>
  </div>
);
